import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import '../styles/auth.css';

/**
 * OAuth callback page that handles the OAuth 2.0 authorization code flow completion
 */
const OAuthCallbackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing OAuth callback...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        // Handle OAuth errors
        if (error) {
          setStatus('error');
          setMessage(errorDescription || error || 'OAuth authorization failed');

          // Send error message to parent window if opened in popup
          if (window.opener) {
            window.opener.postMessage({
              type: 'oauth_callback',
              success: false,
              error: errorDescription || error
            }, window.location.origin);
            window.close();
            return;
          }

          // Redirect to connections page with error after delay
          setTimeout(() => {
            navigate('/connections?oauth_error=' + encodeURIComponent(errorDescription || error));
          }, 3000);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          setStatus('error');
          setMessage('Missing required OAuth parameters');

          if (window.opener) {
            window.opener.postMessage({
              type: 'oauth_callback',
              success: false,
              error: 'Missing OAuth parameters'
            }, window.location.origin);
            window.close();
            return;
          }

          setTimeout(() => {
            navigate('/connections?oauth_error=missing_parameters');
          }, 3000);
          return;
        }

        // Extract flow ID from state parameter
        const flowId = state.split('-')[0];

        setMessage('Completing OAuth authorization...');

        // Send success message to parent window if opened in popup
        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth_callback',
            success: true,
            code,
            state,
            flowId
          }, window.location.origin);

          setStatus('success');
          setMessage('Authorization completed successfully! You can close this window.');

          // Auto-close after a brief delay
          setTimeout(() => {
            window.close();
          }, 2000);
          return;
        }

        // If not in popup, handle the callback directly
        // This would typically involve calling the backend to complete the flow
        // For now, we'll just redirect to the connections page
        setStatus('success');
        setMessage('OAuth authorization completed successfully!');

        setTimeout(() => {
          navigate('/connections?oauth_success=true');
        }, 2000);

      } catch (error) {
        console.error('OAuth callback handling error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred while processing the callback');

        if (window.opener) {
          window.opener.postMessage({
            type: 'oauth_callback',
            success: false,
            error: 'Callback processing error'
          }, window.location.origin);
          window.close();
          return;
        }

        setTimeout(() => {
          navigate('/connections?oauth_error=callback_processing_error');
        }, 3000);
      }
    };

    handleCallback();
  }, [location, navigate]);

  const renderIcon = () => {
    switch (status) {
      case 'processing':
        return <RefreshCw size={48} className="animate-spin text-emerald-500" />;
      case 'success':
        return <CheckCircle size={48} className="text-green-500" />;
      case 'error':
        return <XCircle size={48} className="text-red-500" />;
      default:
        return <RefreshCw size={48} className="animate-spin text-emerald-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-blue-400';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#000000' }}>
      <div
        className="max-w-md w-full mx-4 p-8 rounded-lg"
        style={{
          background: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}
      >
        <div className="text-center">
          <div className="mb-6 flex justify-center">
            {renderIcon()}
          </div>

          <h1 className="text-2xl font-bold text-white mb-4">
            {status === 'processing' && 'Processing Authorization...'}
            {status === 'success' && 'Authorization Successful!'}
            {status === 'error' && 'Authorization Failed'}
          </h1>

          <p className={`text-lg mb-6 ${getStatusColor()}`}>
            {message}
          </p>

          {status === 'processing' && (
            <div className="bg-blue-900/20 border border-blue-800/30 rounded-md p-4">
              <p className="text-blue-400 text-sm">
                Please wait while we complete your OAuth authorization...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-green-900/20 border border-green-800/30 rounded-md p-4">
              <p className="text-green-400 text-sm">
                {window.opener
                  ? 'You can now close this window and return to the main application.'
                  : 'Redirecting you back to the connections page...'}
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="bg-red-900/20 border border-red-800/30 rounded-md p-4">
                <p className="text-red-400 text-sm">
                  The OAuth authorization process encountered an error.
                  {window.opener
                    ? ' You can close this window and try again.'
                    : ' You will be redirected back to try again.'}
                </p>
              </div>

              <button
                onClick={() => {
                  if (window.opener) {
                    window.close();
                  } else {
                    navigate('/connections');
                  }
                }}
                className="btn-primary-dark w-full px-4 py-2 rounded-md transition-colors"
              >
                {window.opener ? 'Close Window' : 'Return to Connections'}
              </button>
            </div>
          )}

          {/* Debug Information (only in development) */}
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-8 text-left">
              <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-200">
                Debug Information
              </summary>
              <div className="mt-2 p-3 bg-white/5 rounded-md text-xs font-mono text-gray-300">
                <div><strong>URL:</strong> {window.location.href}</div>
                <div><strong>Params:</strong> {location.search}</div>
                <div><strong>Status:</strong> {status}</div>
                <div><strong>Has Opener:</strong> {!!window.opener}</div>
                <div><strong>Origin:</strong> {window.location.origin}</div>
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
